import type { CSSProperties, SVGProps } from 'react';
import {
  ApartmentOutlined,
  AuditOutlined,
  HomeOutlined,
  MessageOutlined,
  UserSwitchOutlined,
} from '@ant-design/icons';

const iconStyle: CSSProperties = { width: 20, height: 20, display: 'block' };

type IconProps = SVGProps<SVGSVGElement>;

/** Ant Design outlined icons render a span, so they cannot take SVG props. */
type OutlinedIconProps = {
  className?: string;
  style?: CSSProperties;
};

function Svg({ children, ...props }: IconProps) {
  return (
    <svg
      width={20}
      height={20}
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      style={iconStyle}
      {...props}
    >
      {children}
    </svg>
  );
}

export const SIDE_MENU_ICON_STYLE = iconStyle;

export const USER_MENU_ICON_STYLE: CSSProperties = {
  color: 'var(--color-text-dark, #042a2a)',
  width: 20,
  height: 20,
  margin: 0,
  flexShrink: 0,
};

export const IconHome = ({ style, ...props }: OutlinedIconProps) => (
  <HomeOutlined style={{ ...iconStyle, fontSize: 20, ...style }} {...props} />
);

export const IconReturns = (props: IconProps) => (
  <Svg viewBox="0 0 16 16" {...props}>
    <path
      d="M8.354 10.643a2.54 2.54 0 1 1 0-5.08 2.54 2.54 0 0 1 0 5.08ZM3.691 15.063a1.39 1.39 0 1 1 0-2.787 1.39 1.39 0 0 1 0 2.787ZM1.069 2.61a1.67 1.67 0 1 1 3.344 0 1.67 1.67 0 0 1-3.344 0ZM13.947 3.766a.984.984 0 1 1 0 1.968.984.984 0 0 1 0-1.968Zm-1.39 2.31c.35.366.843.596 1.39.596a1.92 1.92 0 1 0-1.853 2.43l-1.02.683a3.53 3.53 0 0 0-2.72-1.315c-.82 0-1.575.286-2.17.762L4.808 4.2A2.6 2.6 0 0 0 5.35 2.61 2.61 2.61 0 1 0 2.74 5.218c.5 0 .968-.141 1.366-.387l1.43 1.236a3.53 3.53 0 0 0-.66 2.036c0 .874.324 1.674.859 2.285l-.956 1.22a2.33 2.33 0 1 0 .724.596l.94-1.198a3.53 3.53 0 0 0 1.912.574 3.48 3.48 0 0 0 3.477-3.477c0-.481-.098-.94-.275-1.356l1.002-.672Z"
      fill="currentColor"
    />
    <path
      d="M14.034 14.501a.898.898 0 1 1 0-1.795.898.898 0 0 1 0 1.795Zm0-2.733a1.835 1.835 0 1 0 0 3.67 1.835 1.835 0 0 0 0-3.67Z"
      fill="currentColor"
    />
  </Svg>
);

export const IconEntities = ({ style, ...props }: OutlinedIconProps) => (
  <ApartmentOutlined style={{ ...iconStyle, fontSize: 20, ...style }} {...props} />
);

export const IconVies = (props: IconProps) => (
  <Svg viewBox="0 0 18 18" {...props}>
    <path
      d="M14.5 9c0 1.963 0 3.926-.001 5.889 0 .951-.56 1.736-1.385 1.94a2.6 2.6 0 0 1-.38.044c-2.654.002-5.307.003-7.96.001C4.384 16.874 3.676 16.2 3.522 15.212a2.4 2.4 0 0 1-.021-.332C3 10.96 3 7.04 3 3.12 3 2.122 3.601 1.321 4.477 1.15c.097-.019.197-.024.296-.024 2.651-.001 5.302-.002 7.953 0 .853 0 1.55.626 1.732 1.558.03.153.039.314.04.471C14.5 5.103 14.5 7.051 14.5 9Zm-1 .0c0-1.954 0-3.908 0-5.862 0-.523-.32-.885-.785-.885-2.646 0-5.292 0-7.938.001C4.32 2.248 4 2.612 4 3.13c0 3.91 0 7.822 0 11.733 0 .528.32.89.789.89 2.641 0 5.281 0 7.922 0 .474 0 .792-.36.792-.894 0-1.954 0-3.908 0-5.862Z"
      fill="currentColor"
    />
    <path
      d="M8.75 4.498H5.52c-.218 0-.382-.11-.47-.337a.66.66 0 0 1 .09-.603c.103-.13.237-.183.39-.183h6.45c.286 0 .503.219.523.522a.62.62 0 0 1-.508.6H8.75ZM8.75 5.626h3.233c.215 0 .378.106.469.327a.66.66 0 0 1-.074.6c-.103.139-.241.197-.4.196H5.508c-.292 0-.514-.229-.525-.54a.63.63 0 0 1 .507-.582H8.75ZM8.751 8.999H5.525c-.289 0-.509-.224-.524-.53a.63.63 0 0 1 .507-.592h6.466c.29 0 .508.223.524.53a.63.63 0 0 1-.506.592H8.75ZM6.872 11.249H5.497c-.212 0-.37-.12-.454-.34a.66.66 0 0 1 .094-.603c.093-.117.214-.177.353-.177h2.766c.273 0 .49.252.492.556a.5.5 0 0 1-.494.564H6.872Z"
      fill="currentColor"
    />
  </Svg>
);

export const IconUsers = ({ style, ...props }: OutlinedIconProps) => (
  <UserSwitchOutlined style={{ ...iconStyle, fontSize: 20, ...style }} {...props} />
);

export const IconAuditTrail = ({ style, ...props }: OutlinedIconProps) => (
  <AuditOutlined style={{ ...iconStyle, fontSize: 20, ...style }} {...props} />
);

export const IconBilling = (props: IconProps) => (
  <Svg viewBox="0 0 18 18" {...props}>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M16.125 15H1.875C1.04662 15 0.375 14.3284 0.375 13.5V4.5C0.375 3.67162 1.04662 3 1.875 3H16.125C16.9534 3 17.625 3.67162 17.625 4.5V13.5C17.625 14.3284 16.9534 15 16.125 15ZM1.125 7.875H16.875V6.375H1.125V7.875ZM16.875 4.5C16.875 4.086 16.539 3.75 16.125 3.75H1.875C1.461 3.75 1.125 4.086 1.125 4.5V5.625H16.875V4.5ZM16.875 8.625H1.125V13.5C1.125 13.914 1.461 14.25 1.875 14.25H16.125C16.539 14.25 16.875 13.914 16.875 13.5V8.625ZM9.75 10.875H8.25C8.04262 10.875 7.875 10.7074 7.875 10.5C7.875 10.293 8.04262 10.125 8.25 10.125H9.75C9.95737 10.125 10.125 10.293 10.125 10.5C10.125 10.7074 9.95737 10.875 9.75 10.875ZM6.375 10.875H3C2.79262 10.875 2.625 10.7074 2.625 10.5C2.625 10.293 2.79262 10.125 3 10.125H6.375C6.58238 10.125 6.75 10.293 6.75 10.5C6.75 10.7074 6.58238 10.875 6.375 10.875ZM3 12H5.25C5.45738 12 5.625 12.168 5.625 12.375C5.625 12.5824 5.45738 12.75 5.25 12.75H3C2.79262 12.75 2.625 12.5824 2.625 12.375C2.625 12.168 2.79262 12 3 12Z"
      fill="currentColor"
    />
  </Svg>
);

export const IconSupport = ({ style, ...props }: OutlinedIconProps) => (
  <MessageOutlined style={{ ...iconStyle, fontSize: 20, ...style }} {...props} />
);

/**
 * Master newspaper glyph (iconNews.svg), fitted into 20×20.
 * Source art is ~243×196 in a 280×250 Illustrator viewBox; drawing it
 * uncropped at 20×20 left a tiny icon, and the later filled redraw looked too bold.
 */
const NEWS_GLYPH_TRANSFORM = 'translate(1.15 2.86) scale(0.07286) translate(0 -23.427)';

const NewsGlyph = () => (
  <g transform={NEWS_GLYPH_TRANSFORM} fill="currentColor">
    <path d="M235.437 60.699h-31.709V30.927c0-4.142-3.358-7.5-7.5-7.5H7.5c-4.142 0-7.5 3.358-7.5 7.5v161.478c0 14.945 12.159 27.104 27.104 27.104h188.728c14.945 0 27.105-12.159 27.105-27.104V68.199c0-4.142-3.358-7.5-7.5-7.5zM15 192.405V38.427h173.728v29.771v124.206c0 4.349 1.036 8.458 2.864 12.104H27.104C20.43 204.509 15 199.079 15 192.405zm200.832 12.104c-6.674 0-12.104-5.43-12.104-12.104V75.699h24.209v116.706c0 6.674-5.431 12.104-12.105 12.104z" />
    <path d="M39.351 78.374h125.025c4.142 0 7.5-3.358 7.5-7.5s-3.358-7.5-7.5-7.5H39.351c-4.142 0-7.5 3.358-7.5 7.5s3.357 7.5 7.5 7.5z" />
    <path d="M39.008 115.893c0 12.44 10.12 22.561 22.56 22.561 12.438 0 22.558-10.121 22.558-22.561 0-12.441-10.119-22.563-22.558-22.563-12.44 0-22.56 10.122-22.56 22.563zm30.117 0c0 4.169-3.39 7.561-7.558 7.561-4.168 0-7.56-3.392-7.56-7.561 0-4.17 3.391-7.563 7.56-7.563 4.168 0 7.558 3.393 7.558 7.563z" />
    <path d="M39.351 180.02c4.142 0 7.5-3.358 7.5-7.5v-8.361c0-3.899 3.174-7.072 7.075-7.072h15.277c3.903 0 7.078 3.172 7.078 7.072v8.361c0 4.142 3.358 7.5 7.5 7.5s7.5-3.358 7.5-7.5v-8.361c0-12.17-9.904-22.072-22.078-22.072H53.926c-12.172 0-22.075 9.901-22.075 22.072v8.361c0 4.142 3.357 7.5 7.5 7.5z" />
    <path d="M164.376 97.063h-57.763c-4.142 0-7.5 3.358-7.5 7.5s3.358 7.5 7.5 7.5h57.763c4.142 0 7.5-3.358 7.5-7.5s-3.358-7.5-7.5-7.5z" />
    <path d="M164.376 164.443h-57.763c-4.142 0-7.5 3.358-7.5 7.5s3.358 7.5 7.5 7.5h57.763c4.142 0 7.5-3.358 7.5-7.5s-3.358-7.5-7.5-7.5z" />
    <path d="M164.376 130.753h-57.763c-4.142 0-7.5 3.358-7.5 7.5s3.358 7.5 7.5 7.5h57.763c4.142 0 7.5-3.358 7.5-7.5s-3.358-7.5-7.5-7.5z" />
  </g>
);

export const IconNews = (props: IconProps) => (
  <Svg {...props}>
    <NewsGlyph />
  </Svg>
);

export const IconNewsBlinking = (props: IconProps) => (
  <Svg {...props}>
    <NewsGlyph />
    <circle
      cx="16.6"
      cy="3.5"
      r="2.4"
      fill="var(--color-status-danger, #d13131)"
    />
  </Svg>
);

export const IconLogout = (props: IconProps) => (
  <Svg viewBox="0 0 16 16" {...props}>
    <path
      d="M5.067 8H14.667"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M7.933 0.533H3A1.6 1.6 0 0 0 1.333 2.133v11.667A1.6 1.6 0 0 0 3 15.467h4.933"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12.133 5.467 14.667 8l-2.534 2.533"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);
